/**
 * Space Yield Report — Type Definitions
 *
 * Maps the spec's idealized schema to actual DB tables:
 *   - areas (not "buildings")
 *   - units.usable_sqft (not "sqft")
 *   - lease_contracts (1:1 with unit, no contract_periods)
 *   - transient_stays.subtotal for revenue (not total_amount)
 */

// ─── Unit Status (priority order from spec) ──────────────────────────
export type UnitStatus =
  | 'maintenance'           // P0 — dark gray
  | 'occupied_no_contract'  // P1 — red
  | 'expiring'              // P2 — yellow
  | 'underpriced'           // P3 — orange
  | 'leased'                // P4 — green
  | 'month_to_month'        // P5 — purple
  | 'transient'             // P6 — blue
  | 'idle'                  // P7 — light gray

// ─── Display Status (MVP simplified — 5 values) ────────────────────
export type DisplayStatus = 'Leased' | 'M2M' | 'Transient' | 'Idle' | 'No Contract'

/** Map internal 8-value status to 5-value MVP display status */
export function toDisplayStatus(status: UnitStatus): DisplayStatus {
  switch (status) {
    case 'leased':
    case 'expiring':
    case 'underpriced':
      return 'Leased'
    case 'month_to_month':
      return 'M2M'
    case 'transient':
      return 'Transient'
    case 'idle':
    case 'maintenance':
      return 'Idle'
    case 'occupied_no_contract':
      return 'No Contract'
  }
}

export type RevenueSegment = 'lease' | 'transient' | 'idle'

// ─── Revenue Stream Toggles ──────────────────────────────────────────

export type RevenueStreamId = 'lease' | 'transient' | 'fuel'

export const REVENUE_STREAMS: { id: RevenueStreamId; label: string; defaultOn: boolean }[] = [
  { id: 'lease',     label: 'Lease',     defaultOn: true },
  { id: 'transient', label: 'Transient', defaultOn: true },
  { id: 'fuel',      label: 'Fuel',      defaultOn: true },
]

export const DEFAULT_STREAMS: RevenueStreamId[] = ['lease', 'transient']

// ─── Raw Fuel Transaction (from Supabase) ──────────────────────────

export interface RawFuelTransaction {
  id: string
  lease_contract_id: string | null
  transient_stay_id: string | null
  total_amount: number
  quantity_gallons: number
  fueled_at: string
}

// DB unit_type enum values
export type DbUnitType =
  | 'conventional_hangar'
  | 'box_hangar'
  | 't_hangar'
  | 'shade'
  | 'tiedown'
  | 'ramp'
  | 'office'
  | 'storage'
  | 'shop'
  | 'custom'

// DB lease_type enum
export type DbLeaseType = 'long_term' | 'month_to_month' | 'seasonal'

// Group-by options
export type GroupByOption = 'building' | 'type' | 'status' | 'segment' | 'none'

// ─── Space Category System ──────────────────────────────────────────

export interface SpaceCategory {
  id: string
  label: string
  types: DbUnitType[]
  defaultOn: boolean
}

export const SPACE_CATEGORIES: SpaceCategory[] = [
  { id: 'hangars',    label: 'Hangars',    types: ['conventional_hangar', 'box_hangar', 't_hangar'], defaultOn: true },
  { id: 'shade',      label: 'Shade',      types: ['shade'],                                        defaultOn: true },
  { id: 'tiedowns',   label: 'Tiedowns',   types: ['tiedown'],                                      defaultOn: true },
  { id: 'ramp',       label: 'Ramp',       types: ['ramp'],                                         defaultOn: false },
  { id: 'commercial', label: 'Commercial', types: ['office', 'storage', 'shop'],                    defaultOn: false },
]

/** All unit types from default-on categories */
export const DEFAULT_TYPES: DbUnitType[] = SPACE_CATEGORIES
  .filter((c) => c.defaultOn)
  .flatMap((c) => c.types)

/** All known unit types across all categories */
export const ALL_CATEGORY_TYPES: DbUnitType[] = SPACE_CATEGORIES.flatMap((c) => c.types)

// ─── Column Definitions ─────────────────────────────────────────────

export interface ColumnDef {
  id: string
  label: string
  group: 'core' | 'default' | 'extended'
}

export const CORE_COLUMNS: ColumnDef[] = [
  { id: 'unit',   label: 'Unit',       group: 'core' },
  { id: 'status', label: 'Status',     group: 'core' },
  { id: 'yield',  label: 'Revenue/Sqft', group: 'core' },
]

export const DEFAULT_COLUMNS: ColumnDef[] = [
  { id: 'sqft',         label: 'Sqft',          group: 'default' },
  { id: 'tenant',       label: 'Tenant',        group: 'default' },
  { id: 'lease_rate',   label: 'Lease Rate',    group: 'default' },
  { id: 'trans_rev',    label: 'Trans. Rev',    group: 'default' },
]

export const EXTENDED_COLUMNS: ColumnDef[] = [
  { id: 'annual_value',    label: 'Annual Value',          group: 'extended' },
  { id: 'rate_gap',        label: 'Rate Gap %',            group: 'extended' },
  { id: 'vacancy_cost',    label: 'Vacancy Cost',          group: 'extended' },
  { id: 'trans_rate_night', label: 'Trans. Avg Rate/Night', group: 'extended' },
  { id: 'utilization',     label: 'Utilization %',         group: 'extended' },
  { id: 'renewal',         label: 'Renewal Status',        group: 'extended' },
  { id: 'days_to_expiry',  label: 'Days to Expiry',        group: 'extended' },
  { id: 'escalation',      label: 'Escalation %',          group: 'extended' },
]

export const FUEL_COLUMNS: ColumnDef[] = [
  { id: 'fuel_rev',             label: 'Fuel Revenue',      group: 'extended' },
  { id: 'fuel_commitment',      label: 'Fuel Commitment',   group: 'extended' },
  { id: 'total_value',          label: 'Total Value',       group: 'extended' },
  { id: 'total_value_per_sqft', label: 'Total Value/Sqft',  group: 'extended' },
]

/** Fuel column IDs for stream toggle auto-hiding */
export const FUEL_COLUMN_IDS: string[] = FUEL_COLUMNS.map((c) => c.id)

/** Default visible column IDs (core always included) */
export const DEFAULT_VISIBLE_COLUMNS: string[] = [
  ...CORE_COLUMNS.map((c) => c.id),
  ...DEFAULT_COLUMNS.map((c) => c.id),
]

// ─── Report URL parameters ───────────────────────────────────────────
export interface ReportParams {
  period: string             // YYYY-MM
  types: DbUnitType[]        // granular DB unit_type values
  segment: 'all' | RevenueSegment
  group_by: GroupByOption
  status?: UnitStatus        // quick filter from KPI click
  columns: string[]          // visible column IDs
  streams: RevenueStreamId[] // revenue stream toggles (lease/transient/fuel)
}

// ─── Raw query row types (from Supabase) ─────────────────────────────

export interface RawUnit {
  id: string
  unit_code: string
  area_id: string
  location_id: string
  type: DbUnitType | null
  capacity_strategy: string | null
  usable_sqft: number | null
  door_width: number | null
  depth: number | null
  status: string | null
  is_leasable: boolean | null
  areas: {
    id: string
    name: string
    type: string
    total_footprint_sqft: number | null
  } | null
}

export interface RawLease {
  id: string
  unit_id: string | null
  client_id: string
  monthly_rate: number
  type: DbLeaseType
  start_date: string
  end_date: string | null
  status: string
  contract_number: string
  escalation_type: string | null
  escalation_pct: number | null
  auto_renew: boolean | null
  renewal_contacted_date: string | null
  // Fuel commitment fields
  fuel_commitment_gallons_monthly: number | null
  fuel_commitment_gallons_annual: number | null
  fuel_price_per_gallon: number | null
  fuel_type: string | null
  clients: {
    id: string
    company_name: string | null
    first_name: string | null
    last_name: string | null
  } | null
}

export interface RawTransientStay {
  id: string
  unit_id: string | null
  client_id: string
  subtotal: number | null
  nights_count: number | null
  check_in: string
  expected_check_out: string
  actual_check_out: string | null
  status: string
  clients: {
    id: string
    company_name: string | null
    first_name: string | null
    last_name: string | null
  } | null
}

// ─── Raw allocation with aircraft footprint (from Supabase) ─────────

export interface RawAllocationWithFootprint {
  id: string
  unit_id: string
  aircraft_id: string
  aircraft: {
    id: string
    aircraft_type_id: string | null
    aircraft_types: {
      footprint_sqft: number | null
      wingspan: number
      length: number
    } | null
  } | null
}

// ─── Processed unit (output of compute) ──────────────────────────────

export type RenewalStatus = 'Auto-Renew' | 'Contacted' | 'Not Started' | '—'

export interface ProcessedUnit {
  unit_id: string
  unit_code: string
  unit_type: DbUnitType | null
  area_id: string
  area_name: string
  sqft: number
  capacity_strategy: string | null
  status: UnitStatus
  revenue_segment: RevenueSegment
  tenant_name?: string
  lease_rate?: number
  transient_revenue?: number
  yield_per_sqft: number
  lease_end?: string
  days_to_expiry?: number
  lease_type?: DbLeaseType
  contract_number?: string
  is_underpriced: boolean
  is_mtd: boolean
  // Fuel overlay fields
  fuel_revenue?: number
  fuel_gallons?: number
  fuel_commitment_gallons?: number
  fuel_delivered_gallons?: number
  fuel_commitment_pct?: number
  total_tenant_value?: number
  total_value_per_sqft?: number
  // Extended yield fields
  annual_value?: number
  rate_gap_pct?: number
  vacancy_cost?: number
  trans_rate_per_night?: number
  utilization_pct?: number
  transient_overlap_nights?: number
  renewal_status?: RenewalStatus
  escalation_pct?: number
  escalation_type?: string | null
  // Footprint-based occupancy
  occupied_sqft: number
  occupancy_pct: number
}

// ─── Sub-row types for two-tier building table ──────────────────────

export interface LeaseDisplayRow {
  lease_id: string
  unit_code: string
  sqft: number
  status: UnitStatus
  tenant_name: string
  lease_rate: number
  yield_per_sqft: number
  lease_end?: string
  days_to_expiry?: number
  lease_type: DbLeaseType
  contract_number: string
  renewal_status: RenewalStatus
  escalation_pct?: number
  escalation_type?: string | null
  annual_value: number
  is_underpriced: boolean
  rate_gap_pct?: number
  // Fuel overlay
  fuel_revenue?: number
  fuel_commitment_gallons?: number
  fuel_delivered_gallons?: number
  fuel_commitment_pct?: number
}

export interface TransientAggRow {
  stay_count: number
  night_count: number
  sqft: number
  total_revenue: number
  yield_per_sqft: number
  utilization_pct: number
  trans_rate_per_night: number
  // Fuel overlay
  fuel_revenue: number
  fuel_gallons: number
}

export type DisplaySubRow =
  | { kind: 'unit'; data: ProcessedUnit }
  | { kind: 'lease'; data: LeaseDisplayRow }
  | { kind: 'transient_agg'; data: TransientAggRow }
  | { kind: 'section_header'; label: string; detail?: string }

// ─── Grouped output ──────────────────────────────────────────────────

export interface UnitGroup {
  key: string
  label: string
  units: ProcessedUnit[]
  total_units: number
  total_sqft: number
  occupied_sqft: number
  occupancy_pct: number
  lease_revenue: number
  transient_revenue: number
  total_revenue: number
  fuel_revenue: number
  total_tenant_value: number
  avg_yield: number
  yield_comparison: YieldComparison
  // Two-tier building table fields (populated when groupBy === 'building')
  is_shared: boolean
  sub_rows: DisplaySubRow[]
  client_summary: string
  idle_count: number
  earliest_lease_end?: string
  earliest_days_to_expiry?: number
  has_transient_data: boolean
}

// ─── KPI aggregates ──────────────────────────────────────────────────

export interface KpiData {
  total_yield: number
  yield_annualized: number
  lease_revenue: number
  transient_revenue: number
  fuel_revenue: number
  total_tenant_value: number
  occupancy_pct: number
  producing_sqft: number
  total_sqft: number
  producing_units: number
  total_units: number
  opportunity_monthly: number
  opportunity_annualized: number
  idle_units: number
  underpriced_units: number
  expiring_count: number
  expiring_risk: number
  m2m_count: number
}

// ─── Full report result ──────────────────────────────────────────────

// ─── Yield Comparison (facility-level lease vs transient breakdown) ──

export interface YieldComparisonSegment {
  space_yield: number  // $/sqft from lease_rate or transient_revenue
  fuel_yield: number   // $/sqft from fuel_revenue
  sqft: number         // total sqft in this segment
  utilization_pct?: number      // transient: weighted avg utilization
  avg_rate_per_night?: number   // transient: weighted avg nightly rate
  occupied_nights?: number      // transient: total unique occupied nights
}

export interface YieldComparison {
  lease: YieldComparisonSegment
  transient: YieldComparisonSegment
  combined: YieldComparisonSegment
}

// ─── Full report result ──────────────────────────────────────────────

export interface SpaceYieldReport {
  units: ProcessedUnit[]
  groups: UnitGroup[]
  kpi: KpiData
  has_transient_data: boolean
  has_fuel_data: boolean
  is_mtd: boolean
  unitTypeCounts: Record<string, number>
  yield_comparison: YieldComparison
}
